import React, { useEffect, useState } from "react";
import { ListChecks, IndianRupee, Plus, Pencil, Trash2, Upload, Download, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import AskWidget from "@/components/v2/AskWidget";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import DiscussButton from "@/components/v2/DiscussButton";

const OUTCOME_OPTIONS = ["won", "lost", "bid_submitted", "l2_l3", "withdrawn"];
const LOSS_REASON_CATEGORIES = [
  { value: "", label: "—" },
  { value: "pqc_fail", label: "PQC fail" },
  { value: "eligibility_fail", label: "Eligibility fail" },
  { value: "lost_on_price", label: "Lost on price" },
  { value: "lost_on_tech", label: "Lost on tech" },
  { value: "operational_issue", label: "Operational issue" },
  { value: "won_with_issue", label: "Won, with issue" },
  { value: "other", label: "Other" },
];

function ImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const downloadTemplate = async () => {
    try {
      const tpl = await v2Api.imports.historicalTemplate();
      const cols = tpl.columns;
      const example = tpl.example || {};
      const csv = [
        cols.join(","),
        cols.map((c) => {
          const v = (example[c] ?? "").toString().replace(/"/g, '""');
          return /[",\n]/.test(v) ? `"${v}"` : v;
        }).join(","),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "historical-bids-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Template download failed");
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a CSV/XLSX first"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await v2Api.imports.historicalUpload(file);
      setResult(r);
      toast.success(`${r.created} created · ${r.updated} updated · ${r.skipped} skipped`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Bulk import historical bids</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p className="text-slate-600">
            Upload a CSV or Excel file. Use the template — it covers the loss-reason
            taxonomy (PQC fail / Eligibility / Lost on price / Lost on tech /
            Operational issue / Won-with-issue / Other) plus post-award operational notes.
          </p>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-1" /> Download template (CSV)
          </Button>
          <Input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {result && (
            <div className="text-xs bg-slate-50 border rounded p-2 space-y-1">
              <p>
                <strong>{result.created}</strong> created ·{" "}
                <strong>{result.updated}</strong> updated ·{" "}
                <strong>{result.skipped}</strong> skipped
              </p>
              {result.errors?.length > 0 && (
                <ul className="list-disc list-inside text-rose-600 max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                  {result.errors.length > 20 && <li>… +{result.errors.length - 20} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={upload} disabled={busy || !file}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const CLIENT_TYPES = ["central_govt", "state_govt", "psu", "private", "foreign"];
const PORTALS = ["gem", "cppp", "state_portal", "private", "other"];

const blank = () => ({
  bid_name: "",
  client_name: "",
  client_type: "private",
  tender_portal: "gem",
  industry_sector: "",
  rfp_ref: "",
  tender_value_inr: "",
  our_bid_value_inr: "",
  bid_date: "",
  award_date: "",
  bidders_count: "",
  our_rank: "",
  l1_value_inr: "",
  msme_required: false,
  gem_seller_required: false,
  outcome: "won",
  competitor_won: "",
  lessons_learned: "",
  loss_reason_category: "",
  loss_details: "",
  operational_issues_post_award: "",
  // Manual override for the derived first-time flags. null = use
  // derived calculation; true/false = explicit override (migration
  // 20260525aaaa).
  first_time_in_sector: null,
  first_time_with_client: null,
});

function BidDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(initial ? { ...blank(), ...initial } : blank());
  }, [open, initial]);

  const submit = async () => {
    if (!form.bid_name.trim() || !form.client_name.trim()) {
      toast.error("Bid name and client name required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        tender_value_inr: form.tender_value_inr ? Number(form.tender_value_inr) : null,
        our_bid_value_inr: form.our_bid_value_inr ? Number(form.our_bid_value_inr) : null,
        l1_value_inr: form.l1_value_inr ? Number(form.l1_value_inr) : null,
        bidders_count: form.bidders_count ? Number(form.bidders_count) : null,
      };
      if (initial?.id) await v2Api.patchHistoricalBid(initial.id, payload);
      else await v2Api.createHistoricalBid(payload);
      toast.success(initial?.id ? "Updated" : "Added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit historical bid" : "Add historical bid"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Bid name *</Label>
            <Input value={form.bid_name} onChange={set("bid_name")} placeholder="JJM MVS — Khategaon (FY23)" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Client name *</Label>
            <Input value={form.client_name} onChange={set("client_name")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Client type</Label>
            <select value={form.client_type} onChange={set("client_type")} className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {CLIENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Portal</Label>
            <select value={form.tender_portal} onChange={set("tender_portal")} className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {PORTALS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Outcome</Label>
            <select value={form.outcome} onChange={set("outcome")} className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              {OUTCOME_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Industry sector</Label>
            <Input value={form.industry_sector} onChange={set("industry_sector")} placeholder="water / power / pollution" />
            {/* Manual first-time-* overrides. Tri-state: unchecked twice
                = null (use derived), single check = true, third state
                = false. Most users only need the "yes" tick to flag a
                strategic first-of-a-kind regardless of history. */}
            <div className="flex flex-wrap gap-3 mt-2 text-xs">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.first_time_in_sector === true}
                  onChange={(e) => setForm({
                    ...form,
                    first_time_in_sector: e.target.checked ? true : null,
                  })}
                />
                <span className="text-slate-600">First time in this sector</span>
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.first_time_with_client === true}
                  onChange={(e) => setForm({
                    ...form,
                    first_time_with_client: e.target.checked ? true : null,
                  })}
                />
                <span className="text-slate-600">First time with this client</span>
              </label>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">RFP ref</Label>
            <Input value={form.rfp_ref} onChange={set("rfp_ref")} placeholder="Tender no." />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Tender value (INR)</Label>
            <Input type="number" value={form.tender_value_inr} onChange={set("tender_value_inr")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Our bid (INR)</Label>
            <Input type="number" value={form.our_bid_value_inr} onChange={set("our_bid_value_inr")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">L1 value (INR)</Label>
            <Input type="number" value={form.l1_value_inr} onChange={set("l1_value_inr")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Bidders count</Label>
            <Input type="number" value={form.bidders_count} onChange={set("bidders_count")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Our rank</Label>
            <Input value={form.our_rank} onChange={set("our_rank")} placeholder="L1 / L2 / L3 / NA" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Bid date</Label>
            <Input type="date" value={form.bid_date} onChange={set("bid_date")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Award date</Label>
            <Input type="date" value={form.award_date} onChange={set("award_date")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Loss / issue category</Label>
            <select
              value={form.loss_reason_category}
              onChange={set("loss_reason_category")}
              className="w-full px-2 py-1 border rounded-md bg-white text-sm"
            >
              {LOSS_REASON_CATEGORIES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Competitor won (if lost)</Label>
            <Input value={form.competitor_won} onChange={set("competitor_won")} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Loss details</Label>
            <Input value={form.loss_details} onChange={set("loss_details")} placeholder="e.g. L1 quoted 4.5% lower on the WQM line item" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Post-award operational issues (if won-with-issue)</Label>
            <Input value={form.operational_issues_post_award} onChange={set("operational_issues_post_award")} placeholder="e.g. delayed PBG release, performance shortfall" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Lessons learned</Label>
            <Input value={form.lessons_learned} onChange={set("lessons_learned")} placeholder="Why won/lost. Surface in win-prob explainer." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : initial?.id ? "Save" : "Add bid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Size buckets — Indian government tender norms. Maps tender_value_inr
// into coarse bands for filter chips + win-rate slicing.
const SIZE_BUCKETS = [
  { key: "all", label: "Any size", test: () => true },
  { key: "<1cr", label: "< 1 Cr", test: (v) => v != null && v < 1e7 },
  { key: "1-10cr", label: "1 – 10 Cr", test: (v) => v != null && v >= 1e7 && v < 1e8 },
  { key: "10-100cr", label: "10 – 100 Cr", test: (v) => v != null && v >= 1e8 && v < 1e9 },
  { key: ">100cr", label: "> 100 Cr", test: (v) => v != null && v >= 1e9 },
];

const CLIENT_TYPE_CHIPS = [
  { key: "all", label: "Any client" },
  { key: "central_govt", label: "Central Govt" },
  { key: "state_govt", label: "State Govt" },
  { key: "psu", label: "PSU" },
  { key: "private", label: "Private" },
  { key: "foreign", label: "International" },
];

const OUTCOME_CHIPS = [
  { key: "all", label: "All outcomes" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
  { key: "withdrawn", label: "Withdrawn" },
];


export default function HistoricalBidsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  // Filter state — pure FE; doesn't refetch.
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [clientTypeFilter, setClientTypeFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      setItems(await v2Api.historicalBids.list() || []);
    } catch (err) {
      // Surface the actual server detail so users + ops can see what
      // failed, not just a generic "Could not load". 422/500/etc. all
      // bounce into here.
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (err?.message || "Could not load"));
      toast.error(`Historical bids: ${msg}`);
      console.error("historical bids load failed:", err);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (b) => {
    if (!await confirm(`Delete "${b.bid_name}"?`, { title: "Delete bid", confirmLabel: "Delete" })) return;
    try { await v2Api.deleteHistoricalBid(b.id); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-blue-600" /> Historical Bids
          </h1>
          <p className="text-sm text-slate-500">
            Past bids — used by the win-probability scorer as similarity baseline.
            Add bids you've worked on to improve scoring accuracy.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpenImport(true)}>
            <Upload className="w-4 h-4 mr-1" /> Bulk import
          </Button>
          <Button onClick={() => { setEditing(null); setOpenDialog(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Add bid
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400">
          No historical bids yet. Click <em>Add bid</em> to record your first one.
        </CardContent></Card>
      )}

      {/* Filter chips — pure FE; no refetch. Sector is free-text +
          datalist of values seen in `items`. */}
      {!loading && items.length > 0 && (() => {
        const sectors = Array.from(new Set(items.map((b) => b.industry_sector).filter(Boolean))).sort();
        return (
          <Card className="border-[#E2E8F0]">
            <CardContent className="py-3 flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1 flex-wrap">
                {OUTCOME_CHIPS.map((o) => (
                  <button key={o.key} onClick={() => setOutcomeFilter(o.key)}
                    className={`px-2 py-1 rounded border ${outcomeFilter === o.key ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              <span className="text-slate-300">|</span>
              <div className="flex items-center gap-1 flex-wrap">
                {CLIENT_TYPE_CHIPS.map((c) => (
                  <button key={c.key} onClick={() => setClientTypeFilter(c.key)}
                    className={`px-2 py-1 rounded border ${clientTypeFilter === c.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <span className="text-slate-300">|</span>
              <div className="flex items-center gap-1 flex-wrap">
                {SIZE_BUCKETS.map((s) => (
                  <button key={s.key} onClick={() => setSizeFilter(s.key)}
                    className={`px-2 py-1 rounded border ${sizeFilter === s.key ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <span className="text-slate-300">|</span>
              <div className="flex items-center gap-1 flex-1 min-w-[180px]">
                <span className="text-slate-500">Sector:</span>
                <input list="histbid-sector-list" value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                  placeholder="any"
                  className="h-7 text-xs px-2 border rounded flex-1 min-w-0" />
                <datalist id="histbid-sector-list">
                  {sectors.map((s) => <option key={s} value={s} />)}
                </datalist>
                {(outcomeFilter !== "all" || clientTypeFilter !== "all" || sizeFilter !== "all" || sectorFilter) && (
                  <button onClick={() => { setOutcomeFilter("all"); setClientTypeFilter("all"); setSizeFilter("all"); setSectorFilter(""); }}
                    className="text-slate-500 hover:text-rose-600 ml-1" title="Clear filters">
                    ✕
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Pre-compute first-occurrence indices per sector/client so we
          can flag "First time in sector" / "First time with client"
          badges on each row. Earliest by bid_date string sort. */}
      {(() => null)()}

      <div className="space-y-2">
        {(() => {
          // Apply filters.
          const sizeTest = SIZE_BUCKETS.find((s) => s.key === sizeFilter)?.test || (() => true);
          const sectorQ = sectorFilter.trim().toLowerCase();
          const filtered = items.filter((b) => {
            if (outcomeFilter !== "all" && (b.outcome || "").toLowerCase() !== outcomeFilter) return false;
            if (clientTypeFilter !== "all" && b.client_type !== clientTypeFilter) return false;
            if (!sizeTest(b.tender_value_inr)) return false;
            if (sectorQ && !(b.industry_sector || "").toLowerCase().includes(sectorQ)) return false;
            return true;
          });
          // First-time flags computed over the FULL items list, not the
          // filtered view (we want "first ever in this sector", not
          // "first in the current filter").
          const seenSectors = new Map();   // sector -> earliest bid_date
          const seenClients = new Map();   // client_name -> earliest bid_date
          const sortedByDate = [...items].sort((a, b) =>
            (a.bid_date || "").localeCompare(b.bid_date || "")
          );
          for (const b of sortedByDate) {
            const sec = (b.industry_sector || "").trim().toLowerCase();
            const cli = (b.client_name || "").trim().toLowerCase();
            if (sec && !seenSectors.has(sec)) seenSectors.set(sec, b.id);
            if (cli && !seenClients.has(cli)) seenClients.set(cli, b.id);
          }
          if (filtered.length === 0 && items.length > 0) {
            return (
              <Card><CardContent className="py-8 text-center text-slate-400 text-sm">
                No historical bids match the current filters.
              </CardContent></Card>
            );
          }
          return filtered.map((b) => {
            const won = (b.outcome || "").toLowerCase() === "won";
            const sec = (b.industry_sector || "").trim().toLowerCase();
            const cli = (b.client_name || "").trim().toLowerCase();
            // Prefer the manual override (migration 20260525aaaa) when
            // set; fall back to the FE-derived calculation otherwise.
            const firstInSector = b.first_time_in_sector != null
              ? b.first_time_in_sector
              : (sec && seenSectors.get(sec) === b.id);
            const firstWithClient = b.first_time_with_client != null
              ? b.first_time_with_client
              : (cli && seenClients.get(cli) === b.id);
            return (
              <Card key={b.id} className={won ? "border-emerald-200" : "border-rose-200"}>
                <CardContent className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate">{b.bid_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {b.client_name} · {b.client_type} {b.bid_date ? `· ${b.bid_date}` : ""}
                      {b.industry_sector ? ` · ${b.industry_sector}` : ""}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {firstInSector && (
                        <Badge className="bg-purple-100 text-purple-800 text-[10px]">
                          First time in this sector
                        </Badge>
                      )}
                      {firstWithClient && (
                        <Badge className="bg-blue-100 text-blue-800 text-[10px]">
                          First time with this client
                        </Badge>
                      )}
                      {b.client_type === "foreign" && (
                        <Badge className="bg-indigo-100 text-indigo-800 text-[10px]">
                          International
                        </Badge>
                      )}
                    </div>
                    {b.lessons_learned && (
                      <p className="text-xs text-slate-500 mt-1 italic line-clamp-2">{b.lessons_learned}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0 space-y-1">
                    <Badge className={won ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>
                      {b.outcome}
                    </Badge>
                    {b.our_bid_value_inr && (
                      <p className="text-xs font-mono text-slate-700 flex items-center gap-1 justify-end">
                        <IndianRupee className="w-3 h-3" />
                        {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(b.our_bid_value_inr)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <DiscussButton
                      entityType="historical_bid"
                      entityId={b.id}
                      title={b.bid_name}
                    />
                    <AttachmentMenu bid={b} onChange={refresh} />
                    <button onClick={() => { setEditing(b); setOpenDialog(true); }}
                      className="text-slate-300 hover:text-blue-500" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(b)}
                      className="text-slate-300 hover:text-rose-500" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          });
        })()}
      </div>

      <BidDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        initial={editing}
        onSaved={refresh}
      />
      <ImportDialog
        open={openImport}
        onOpenChange={setOpenImport}
        onDone={refresh}
      />
      <AskWidget
        title="Ask history"
        placeholder="Ask about past bids, outcomes, sectors…"
        examples={[
          "What did we propose for refinery flare monitoring last year?",
          "Which bids did we lose to Forbes Marshall?",
          "List wins above ₹5 Cr in Oil & Gas.",
        ]}
        askFn={(q, k) => v2Api.historical.ask(q, k)}
        onCitationClick={(c) => { if (c.deep_link) window.location.href = c.deep_link; }}
        open={askOpen}
        onOpen={() => setAskOpen(true)}
        onClose={() => setAskOpen(false)}
        buttonColor="bg-violet-600 hover:bg-violet-700"
      />
    </div>
  );
}


/** AttachmentMenu — per-row attach/download/delete for RFP doc + proposal.
 *
 * Uses native <input type="file"> hidden behind a paperclip icon. On
 * select, POSTs to /historical-bids/{id}/attachment?kind=...; on click
 * over an existing attachment, opens a download URL with the bearer
 * token in the Authorization header (fetch + blob + anchor, can't use
 * a plain <a> because the URL needs auth).
 */
function AttachmentMenu({ bid, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);   // "rfp" | "proposal" | null

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      await v2Api.uploadHistoricalAttachment(bid.id, kind, file);
      toast.success(`${kind === "rfp" ? "RFP" : "Proposal"} attached`);
      onChange?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const download = async (kind) => {
    try {
      const url = v2Api.historicalAttachmentUrl(bid.id, kind);
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `${kind}.bin`;
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(obj);
    } catch (e) {
      toast.error(`Download failed: ${e?.message || e}`);
    }
  };

  const remove = async (kind) => {
    if (!await confirm(`Detach the ${kind === "rfp" ? "RFP" : "proposal"} document?`, { title: "Detach document", confirmLabel: "Detach" })) return;
    try {
      await v2Api.deleteHistoricalAttachment(bid.id, kind);
      toast.success("Detached");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Detach failed");
    }
  };

  const hasRfp = !!bid.rfp_doc_path;
  const hasProposal = !!bid.proposal_doc_path;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-slate-300 hover:text-blue-500 relative ${(hasRfp || hasProposal) ? "text-blue-500" : ""}`}
        title="Attachments"
      >
        <Paperclip className="w-4 h-4" />
        {(hasRfp || hasProposal) && (
          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center">
            {(hasRfp ? 1 : 0) + (hasProposal ? 1 : 0)}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-6 top-0 z-20 w-64 rounded-md border bg-white shadow-lg text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <span className="font-medium text-slate-800">Attachments</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
              <X className="w-3 h-3" />
            </button>
          </div>
          {["rfp", "proposal"].map((kind) => {
            const has = kind === "rfp" ? hasRfp : hasProposal;
            const label = kind === "rfp" ? "RFP document" : "Bidder proposal";
            return (
              <div key={kind} className="px-3 py-2 border-b last:border-b-0">
                <p className="text-slate-700 font-medium mb-1">{label}</p>
                {has ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => download(kind)}
                      className="flex-1 text-left text-blue-600 hover:underline inline-flex items-center gap-1">
                      <Download className="w-3 h-3" /> Download
                    </button>
                    <button onClick={() => remove(kind)} className="text-rose-500 hover:text-rose-700" title="Detach">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="block text-slate-600 cursor-pointer hover:text-blue-600">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.md,.zip"
                      disabled={busy === kind}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload(kind, f);
                        e.target.value = "";
                      }}
                    />
                    <span className="inline-flex items-center gap-1">
                      <Upload className="w-3 h-3" />
                      {busy === kind ? "Uploading…" : "Attach file"}
                    </span>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
